import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import s from './Item.module.scss';
import { ReactComponent as Edit } from 'images/edit.svg';
import { ReactComponent as Remove } from 'images/remove.svg';
import { useToggle } from 'lib/hooks/useToggle';

import { useOutsideClick } from 'lib/hooks/useOutsideClick';
import { Button } from 'components/common/Button';
import { ReactComponent as MenuIcon } from 'images/menu.svg';
import {
  ContextMenu,
  ContextMenuOption,
} from 'components/common/ContextMenu/ContextMenu';
import { PortalComponent } from 'components/common/PortalComponent';
import { Project } from 'types/project';
import { useTranslation } from 'react-i18next';

const icons = {
  edit: <Edit className={s['context-menu__item__icon']} />,
  delete: <Remove className={s['context-menu__item__icon']} />,
  menu: <MenuIcon />,
};

type ItemProps = {
  projectId: Project['id'];
  name: Project['name'];
  onClick?: (projectId: Project['id']) => void;
  isSelected?: boolean;
  onDelete: (projectId: Project['id'], isSelected?: boolean) => Promise<void>;
  onEdit: (projectId: Project['id']) => void;
};

const MENU_HEIGHT = 180;
const VIEWPORT_PADDING = 8;

export const Item: FC<ItemProps> = memo(
  ({ projectId, name, onClick, onDelete, onEdit }) => {
    const { t } = useTranslation();
    const menuBtnRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);

    const [isOpenMenu, setIsOpenMenu] = useToggle(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

    const handleOpenMenu = (e: React.MouseEvent) => {
      e.stopPropagation();

      const rect = menuBtnRef.current?.getBoundingClientRect();
      if (!rect) return;

      const hasSpaceBelow =
        window.innerHeight - rect.bottom >= MENU_HEIGHT + VIEWPORT_PADDING;

      setMenuPos({
        top: hasSpaceBelow
          ? rect.bottom
          : Math.max(VIEWPORT_PADDING, rect.top - MENU_HEIGHT + 15),
        left: Math.max(VIEWPORT_PADDING, rect.left),
      });

      setIsOpenMenu(true);
    };

    const handleCloseMenu = useCallback(() => {
      setIsOpenMenu(false);
    }, [setIsOpenMenu]);

    const optionsMenu = useMemo<ContextMenuOption[]>(
      () => [
        {
          id: 0,
          children: (
            <div className={s['context-menu__item']}>
              {icons.edit}
              <span className={s['context-menu__item__name']}>
                {t('contextMenu.edit')}
              </span>
            </div>
          ),
          onClick: () => {
            onEdit(projectId);
            handleCloseMenu();
          },
        },

        {
          id: 1,
          children: (
            <div className={s['context-menu__item']}>
              {icons.delete}
              <span className={s['context-menu__item__name']}>
                {t('contextMenu.remove')}
              </span>
            </div>
          ),
          onClick: () => {
            onDelete(projectId);
            handleCloseMenu();
          },
        },
      ],
      [projectId, onDelete, onEdit, handleCloseMenu, t],
    );

    useOutsideClick({
      ref: menuRef,
      handlerClose: handleCloseMenu,
      attached: isOpenMenu,
      capture: true,
    });

    return (
      <>
        <li className={s.item} onClick={() => onClick?.(projectId)}>
          <div className={s['item__icon']}>{name[0].toUpperCase()}</div>

          <span className={s.item__name}>{name}</span>
          <Button
            ref={menuBtnRef}
            className={s['item__menu-btn']}
            onClick={handleOpenMenu}
          >
            {icons.menu}
          </Button>
        </li>

        {isOpenMenu && (
          <PortalComponent>
            <div
              ref={menuRef}
              style={{
                position: 'fixed',
                top: menuPos.top,
                left: menuPos.left,
                zIndex: 3,
              }}
            >
              <ContextMenu
                className={s['context-menu']}
                options={optionsMenu}
              />
            </div>
          </PortalComponent>
        )}
      </>
    );
  },
);

Item.displayName = 'Item';
